import {
  CrateControllerFindCuratedCratesRequest,
  GetCratesDto,
  GetSimpleCrateDto,
} from '@cratehackers/api-client';
import { IonButton, IonCard, IonCardContent } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIcons } from '../../../hooks/useIcons';
import {
  WidgetListRowsSkeleton,
  WidgetTabPillsSkeleton,
} from '../../dashboard/HomepageSkeletons';
import ApiService from '../../../services/Api.service';
import { CrateService } from '../../../services/Crate.service';
import '../Crates.scss';
import './CrateWidgets.scss';

type CrateItem = {
  id: string;
  title: string;
  artUrl: string;
  hackers?: string;
  trackCount?: number;
  publishedMs?: number;
};

function formatHackerNames(hackerList: unknown[]): string {
  return hackerList
    .map((h: any) => {
      let name =
        h.name ||
        `${h.First_Name || h.firstName || ''} ${h.Last_Name || h.lastName || ''}`.trim();
      if (name.toLowerCase() === 'dynamix dynamix') {
        name = 'Dynamix';
      }
      return name;
    })
    .filter(Boolean)
    .join(', ');
}

function publishedMsFromRaw(raw: unknown): number | undefined {
  if (!raw) return undefined;
  if (raw instanceof Date) return raw.getTime();
  if (typeof raw === 'number') return raw;
  const parsed = Date.parse(String(raw));
  return Number.isNaN(parsed) ? undefined : parsed;
}

function getPublishedMs(crate: Record<string, unknown>): number | undefined {
  return publishedMsFromRaw(crate.published ?? crate.Published);
}

function mapCuratedCrateToItem(crate: GetSimpleCrateDto): CrateItem {
  const hackerList = (crate.hacker as unknown[]) || [];
  return {
    id: crate.id,
    title: crate.title,
    artUrl: crate.image,
    hackers: formatHackerNames(hackerList) || undefined,
    trackCount: crate.trackCount,
    publishedMs: publishedMsFromRaw(crate.published),
  };
}

function mapLegacyCrateToItem(crate: Record<string, unknown>): CrateItem {
  const hackerList =
    (crate.hacker as unknown[]) || (crate.Hacker as unknown[]) || [];
  return {
    id: String(crate.id ?? crate._id ?? ''),
    title: String(crate.title ?? crate.Title ?? ''),
    artUrl: String(crate.image ?? crate.Image ?? ''),
    hackers: formatHackerNames(hackerList) || undefined,
    trackCount:
      typeof crate.trackCount === 'number' ? crate.trackCount : undefined,
    publishedMs: getPublishedMs(crate),
  };
}

function formatCrateWidgetDate(publishedMs: number): string {
  return new Date(publishedMs).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const CrateRow: React.FC<{
  item: CrateItem;
  editing: boolean;
  fallbackArt: string;
}> = ({ item, editing, fallbackArt }) => {
  const history = useHistory();
  let artSrc = item.artUrl || fallbackArt;
  if (artSrc.startsWith('//')) artSrc = `https:${artSrc}`;
  else if (
    artSrc &&
    !artSrc.startsWith('http://') &&
    !artSrc.startsWith('https://') &&
    /^[\w.-]+\.[a-z]{2,}/i.test(artSrc)
  ) {
    artSrc = `https://${artSrc}`;
  }

  const publishedLabel =
    item.publishedMs != null ? formatCrateWidgetDate(item.publishedMs) : '';

  return (
    <button
      type="button"
      className="crate-item"
      disabled={editing}
      onClick={() => history.push(`/crate/${item.id}`)}
    >
      <img src={artSrc || fallbackArt} alt="" className="crate-image" />
      <span className="crate-copy">
        <span className="crate-title">{item.title}</span>
        {item.hackers && <span className="crate-hacker">{item.hackers}</span>}
      </span>
      <span className="crate-meta">
        {typeof item.trackCount === 'number' && (
          <span className="crate-meta-line">
            <span className="crate-track-count">{item.trackCount}</span>
            <span className="crate-track-unit"> tracks</span>
          </span>
        )}
        {publishedLabel && <span className="crate-date">{publishedLabel}</span>}
      </span>
    </button>
  );
};

interface CrateListWidgetProps {
  items: CrateItem[];
  loading: boolean;
  label: string;
  editing: boolean;
  viewAllLink?: string;
}

export const CrateListWidget: React.FC<CrateListWidgetProps> = ({
  items,
  loading,
  label,
  editing,
  viewAllLink,
}) => {
  const { icons, loading: iconsLoading } = useIcons();

  return (
    <>
      {loading ? (
        <IonCard className="modern-widget-card shimmer-card">
          <IonCardContent className="shimmer-content"></IonCardContent>
        </IonCard>
      ) : (
        <IonCard className="modern-widget-card crate-list-card">
          <IonCardContent className="modern-widget-content">
            <div className="widget-header">
              <h3 className="widget-title">{label}</h3>
              {viewAllLink && (
                <IonButton
                  fill="clear"
                  size="small"
                  routerLink={viewAllLink}
                  className="view-all-button"
                >
                  All
                </IonButton>
              )}
            </div>
            <div className="crates-list-content">
              <div className="crates-list">
                {items.map((c) => (
                  <CrateRow
                    key={c.id}
                    item={c}
                    editing={editing}
                    fallbackArt={icons.flameLogo}
                  />
                ))}
              </div>
            </div>
          </IonCardContent>
        </IonCard>
      )}
    </>
  );
};

// My Crates widget
export const MyCratesWidget: React.FC<{
  editing: boolean;
  numShown: number;
}> = ({ editing, numShown = 10 }) => {
  const [items, setItems] = useState<CrateItem[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    CrateService.fetchSelectedCrates([CrateService.MY_CRATES], (crates) => {
      crates = crates.slice(0, numShown);
      console.log(crates);
      setItems(
        crates.map((c) => mapLegacyCrateToItem(c as Record<string, unknown>)),
      );
      setLoading(false);
    });
  }, []);
  return (
    <CrateListWidget
      items={items}
      loading={loading}
      editing={editing}
      label="My Recent Crates"
      viewAllLink="/my-crates"
    />
  );
};

// VIP Crates widget - shows crates from specific creators
const VIP_CREATORS = ['Nate Acosta', 'Nick Spinelli', 'Dynamix Dynamix'];

export const VIPCratesWidget: React.FC<{
  editing: boolean;
  numShown: number;
}> = ({ editing, numShown = 10 }) => {
  const [items, setItems] = useState<CrateItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const api = ApiService.getApiClient();
    const req: CrateControllerFindCuratedCratesRequest = {
      cursor: 0,
      archived: 'false',
    };
    api
      .crateControllerFindCuratedCrates(req)
      .then((res: GetCratesDto) => {
        // Filter for VIP creators and Standard type only
        const vipCrates = res.results.filter((c: any) => {
          const crateType = c.type || c.Type || '';
          const isStandard = crateType === 'Standard';
          const hackers = c.hacker || c.Hacker || [];
          const isVIPCreator = hackers.some((h: any) => {
            const name =
              h.name ||
              `${h.First_Name || h.firstName || ''} ${h.Last_Name || h.lastName || ''}`.trim();
            return VIP_CREATORS.some(
              (vip) =>
                name.toLowerCase() === vip.toLowerCase() ||
                name.toLowerCase().includes(vip.toLowerCase()),
            );
          });
          return isStandard && isVIPCreator;
        });

        setItems(vipCrates.slice(0, numShown).map(mapCuratedCrateToItem));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [numShown]);

  return (
    <CrateListWidget
      items={items}
      loading={loading}
      editing={editing}
      label="VIP Crates"
      viewAllLink="/crates"
    />
  );
};

// Recent Hackathon/Curated Crates widget
export const CuratedCratesWidget: React.FC<{
  editing: boolean;
  numShown: number;
}> = ({ editing, numShown = 10 }) => {
  const [items, setItems] = useState<CrateItem[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const api = ApiService.getApiClient();
    const req: CrateControllerFindCuratedCratesRequest = {
      cursor: 0,
      archived: 'false',
    };
    api
      .crateControllerFindCuratedCrates(req)
      .then((res: GetCratesDto) => {
        const crates = res.results.slice(0, numShown);
        setItems(crates.map(mapCuratedCrateToItem));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);
  return (
    <CrateListWidget
      items={items}
      loading={loading}
      editing={editing}
      label="Recent Curated Crates"
      viewAllLink="/crates"
    />
  );
};

// Combined Tabbed Crates Widget (VIP + Recent Curated)
export const TabbedCratesWidget: React.FC<{
  editing: boolean;
  numShown: number;
}> = ({ editing, numShown = 10 }) => {
  const [activeTab, setActiveTab] = useState<'vip' | 'recent'>('recent');
  const [vipItems, setVipItems] = useState<CrateItem[]>([]);
  const [recentItems, setRecentItems] = useState<CrateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { icons } = useIcons();

  useEffect(() => {
    const api = ApiService.getApiClient();
    const req: CrateControllerFindCuratedCratesRequest = {
      cursor: 0,
      archived: 'false',
    };
    api
      .crateControllerFindCuratedCrates(req)
      .then((res: GetCratesDto) => {
        // VIP Crates
        const vipCrates = res.results.filter((c: any) => {
          const crateType = c.type || c.Type || '';
          const isStandard = crateType === 'Standard';
          const hackers = c.hacker || c.Hacker || [];
          const isVIPCreator = hackers.some((h: any) => {
            const name =
              h.name ||
              `${h.First_Name || h.firstName || ''} ${h.Last_Name || h.lastName || ''}`.trim();
            return VIP_CREATORS.some(
              (vip) =>
                name.toLowerCase() === vip.toLowerCase() ||
                name.toLowerCase().includes(vip.toLowerCase()),
            );
          });
          return isStandard && isVIPCreator;
        });

        setVipItems(vipCrates.slice(0, numShown).map(mapCuratedCrateToItem));

        // Recent Curated Crates
        setRecentItems(
          res.results.slice(0, numShown).map(mapCuratedCrateToItem),
        );

        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [numShown]);

  const currentItems = activeTab === 'vip' ? vipItems : recentItems;

  return (
    <IonCard className="modern-widget-card crate-list-card tabbed-crates-card">
      <IonCardContent className="modern-widget-content">
        <div className="widget-header">
          <h3 className="widget-title">Curated Crates</h3>
          {!loading && (
            <IonButton
              fill="clear"
              size="small"
              routerLink="/crates"
              className="view-all-button"
            >
              All
            </IonButton>
          )}
        </div>
        {loading ? (
          <>
            <WidgetTabPillsSkeleton />
            <div className="crates-list-content">
              <div className="crates-list">
                <WidgetListRowsSkeleton count={numShown} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="crate-tabs">
              <button
                type="button"
                className={`crate-tab ${activeTab === 'recent' ? 'active' : ''}`}
                onClick={() => setActiveTab('recent')}
              >
                Recent Curated
              </button>
              <button
                type="button"
                className={`crate-tab ${activeTab === 'vip' ? 'active' : ''}`}
                onClick={() => setActiveTab('vip')}
              >
                VIP Crates
              </button>
            </div>
            <div className="crates-list-content">
              <div className="crates-list">
                {currentItems.map((c) => (
                  <CrateRow
                    key={c.id}
                    item={c}
                    editing={editing}
                    fallbackArt={icons.flameLogo}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </IonCardContent>
    </IonCard>
  );
};
